import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import { supabase } from '@/lib/supabase';
import { mascaraTelefone } from '@/pages/inscricao/utils';
import { irParaOPrimeiro, validar, type CampoGuiado } from '@/lib/formularioGuiado';
import SeletorQuantidade from '@/pages/talao/components/SeletorQuantidade';
import { formatarReais, pad4, totalDaConta } from '@/pages/talao/utils';
import ErrorBoundary from '@/components/base/ErrorBoundary';
import type { MeuTalao, PixRifaVenda } from '../types';

const INTERVALO_CHECAR_MS = 5000;

type EtapaVenda = 'nova' | 'pix' | 'pronto';

interface Props {
  t: MeuTalao;
  rifaId: number;
  vendedorId: number | null;
  aoSair: () => void;
  aoConcluir: () => void;
}

function waNumero(telefone: string): string {
  const d = telefone.replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && d.length >= 12 && d.length <= 13) return d;
  return `55${d}`;
}

export default function Venda({ t, rifaId, vendedorId, aoSair, aoConcluir }: Props) {
  // A chave nasce quando o formulário abre (ESTADO 2) e morre quando a venda
  // termina ou é cancelada. Nunca reaproveitamos a chave de uma venda encerrada.
  const [chave, setChave] = useState(() => crypto.randomUUID());

  const [etapa, setEtapa] = useState<EtapaVenda>('nova');

  const [quantidade, setQuantidade] = useState(1);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<{ nome?: string; whatsapp?: string }>({});

  const [pix, setPix] = useState<PixRifaVenda | null>(null);
  const [numerosVendidos, setNumerosVendidos] = useState<number[]>([]);
  const [copiado, setCopiado] = useState(false);

  const livres = useMemo(
    () => (t.numeros || []).filter((n) => n.status === 'livre').length,
    [t],
  );

  const valorCota = t.valor_cota_reais ?? 0;
  const totalPrevisto = valorCota * quantidade;

  async function gerarPix() {
    const errosNovos = {
      nome: nome.trim() ? undefined : 'Falta o nome de quem está comprando.',
      whatsapp:
        telefone.replace(/\D/g, '').length >= 10
          ? undefined
          : 'Informe o WhatsApp de quem está comprando.',
    };
    setErros(errosNovos);

    const campos: CampoGuiado[] = [
      { ref: document.getElementById('venda-nome'), rotulo: 'Nome', mensagem: errosNovos.nome ?? null },
      { ref: document.getElementById('venda-whats'), rotulo: 'WhatsApp', mensagem: errosNovos.whatsapp ?? null },
    ];
    const invalidos = validar(campos);
    if (invalidos.length > 0) {
      irParaOPrimeiro(invalidos);
      return;
    }

    setGerando(true);
    setErro(null);
    try {
      const { data } = await supabase.functions.invoke('criar-pix-rifa', {
        body: {
          codigo: t.codigo,
          quantidade,
          nome: nome.trim(),
          telefone: telefone.replace(/\D/g, ''),
          chave,
        },
      });

      const resposta = (data ?? {}) as PixRifaVenda;

      if (resposta.ok === false) {
        const msg =
          typeof resposta.erro === 'string' && resposta.erro
            ? resposta.erro
            : 'Não foi possível. Tente de novo.';
        setErro(msg);
        if (resposta.ainda_tem && Number.isFinite(resposta.ainda_tem) && resposta.ainda_tem >= 1) {
          setQuantidade(Math.min(resposta.ainda_tem, Math.max(1, livres) || resposta.ainda_tem));
        }
        return;
      }

      const numeros = Array.isArray(resposta.numeros) ? resposta.numeros : [];
      setNumerosVendidos(numeros);
      setPix(resposta);
      setEtapa('pix');
    } catch (e) {
      console.error('[NovaVenda]', e);
      setErro(e instanceof Error ? e.message : 'Não foi possível. Tente de novo.');
    } finally {
      setGerando(false);
    }
  }

  // ESTADO 3 → 4: checa a cada 5s até todos os números virarem 'paga'.
  useEffect(() => {
    if (etapa !== 'pix' || numerosVendidos.length === 0) return;

    async function checar() {
      try {
        const params: Record<string, number> = { p_rifa: rifaId };
        if (vendedorId != null) params.p_vendedor = vendedorId;
        const { data } = await supabase.rpc('meu_talao', params);
        const talao = (data as MeuTalao) ?? null;
        if (!talao) return;
        const todosPagos = numerosVendidos.every((num) => {
          const n = (talao.numeros || []).find((x) => x.numero === num);
          return n?.status === 'paga';
        });
        if (todosPagos) {
          setChave('');
          setEtapa('pronto');
          aoConcluir();
        }
      } catch (e) {
        console.error('[NovaVenda]', e);
      }
    }

    const intervalo = window.setInterval(checar, INTERVALO_CHECAR_MS);
    return () => window.clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa, numerosVendidos]);

  async function cancelar() {
    // Devolve os números reservados para o talão (a ação de hoje).
    if (etapa === 'pix' && numerosVendidos.length > 0) {
      try {
        await supabase.rpc('cancelar_venda', { p_rifa: rifaId, p_numeros: numerosVendidos });
      } catch (e) {
        console.error('[NovaVenda]', e);
      }
    }
    setChave('');
    aoSair();
  }

  function venderDeNovo() {
    setChave(crypto.randomUUID());
    setQuantidade(1);
    setNome('');
    setTelefone('');
    setErro(null);
    setErros({});
    setPix(null);
    setNumerosVendidos([]);
    setEtapa('nova');
  }

  async function copiarCodigo() {
    if (!pix?.pix_copia_e_cola) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_e_cola);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* não bloqueia */
    }
  }

  function mandarPixWhats() {
    const numero = waNumero(telefone);
    if (!numero || !pix?.pix_copia_e_cola) return;
    window.open(
      `https://wa.me/${numero}?text=${encodeURIComponent(pix.pix_copia_e_cola)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  function mandarNumerosWhats() {
    const numero = waNumero(telefone);
    if (!numero) return;
    const primeiro = nome.trim() ? nome.trim().split(/\s+/)[0] : 'você';
    const texto = `Olá, ${primeiro}! Seus números da ação entre amigos: ${numerosVendidos.map((n) => pad4(n)).join(', ')}.`;
    window.open(
      `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }

  const tituloModal =
    etapa === 'pronto' ? 'Venda confirmada' : etapa === 'pix' ? 'Mostre o Pix' : 'Nova venda';

  // ---- ESTADO 4 · PRONTO ----
  const conteudoPronto = (
    <div className="text-center">
      <span className="animate-pulso mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-success-500 text-white">
        <i className="ri-check-line text-5xl" />
      </span>
      <h1 className="mt-5 text-xl font-bold text-foreground-950">
        Pago!{' '}
        {numerosVendidos.length > 0
          ? `Os números ${numerosVendidos.map((n) => pad4(n)).join(' e ')} são ${nome.trim() || 'do comprador'}.`
          : 'Venda confirmada!'}
      </h1>

      <button
        type="button"
        onClick={mandarNumerosWhats}
        disabled={!waNumero(telefone)}
        className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-whatsapp-line" />
        </span>
        Mandar os números para {nome.trim() ? nome.trim().split(/\s+/)[0] : 'o comprador'}
      </button>

      <button
        type="button"
        onClick={venderDeNovo}
        className="mt-2 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-6 py-3.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-50"
      >
        Nova venda
      </button>
    </div>
  );

  // ---- ESTADO 3 · PIX ----
  const conteudoPix = (
    <div className="text-center">
      <p className="text-sm font-semibold text-foreground-900">
        {numerosVendidos.length > 0
          ? `Números: ${numerosVendidos.map((n) => pad4(n)).join(', ')}`
          : 'Mostre o Pix para o comprador'}
      </p>

      <p className="mt-3 text-3xl font-extrabold text-foreground-950">
        {pix ? (totalDaConta(pix.detalhe, pix.total_reais) ?? formatarReais(totalPrevisto)) : formatarReais(totalPrevisto)}
      </p>

      <div className="mt-4 flex justify-center">
        {pix?.pix_qrcode_base64 ? (
          <img
            src={`data:image/png;base64,${pix.pix_qrcode_base64}`}
            alt="QR Code Pix"
            width={220}
            height={220}
            className="rounded-2xl border border-secondary-200"
          />
        ) : (
          <div className="flex h-[220px] w-[220px] items-center justify-center rounded-2xl border border-secondary-200 bg-secondary-50 text-secondary-400">
            <i className="ri-qr-code-line text-5xl" />
          </div>
        )}
      </div>

      <p className="mt-3 text-sm text-secondary-600">O comprador escaneia e paga no Pix.</p>

      <div className="mt-4 space-y-2">
        {pix?.pix_copia_e_cola && (
          <button
            type="button"
            onClick={copiarCodigo}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
            </span>
            {copiado ? 'Copiado!' : 'Copiar código Pix'}
          </button>
        )}

        <button
          type="button"
          onClick={mandarPixWhats}
          disabled={!waNumero(telefone) || !pix?.pix_copia_e_cola}
          className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-whatsapp-line" />
          </span>
          Mandar o Pix no WhatsApp
        </button>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-secondary-600">
        <span className="flex h-4 w-4 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin" />
        </span>
        Aguardando o pagamento…
      </div>

      <button
        type="button"
        onClick={cancelar}
        className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
      >
        Cancelar venda
      </button>
    </div>
  );

  // ---- ESTADO 2 · NOVA VENDA ----
  const conteudoNova = (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground-800">Quantos números?</p>
        <p className="text-lg font-bold text-foreground-950">{formatarReais(totalPrevisto)}</p>
      </div>

      <div className="mt-3">
        <SeletorQuantidade quantidade={quantidade} maximo={Math.max(1, livres)} aoMudar={setQuantidade} />
      </div>

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor="venda-nome" className="text-sm font-medium text-foreground-800">
            Quem está comprando?
          </label>
          <input
            id="venda-nome"
            type="text"
            value={nome}
            onChange={(e) => {
              setNome(e.target.value);
              setErros((er) => ({ ...er, nome: undefined }));
            }}
            placeholder="Nome"
            className={`mt-1.5 w-full rounded-2xl border bg-white px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 ${
              erros?.nome ? 'border-danger-500' : 'border-secondary-300'
            }`}
          />
          {erros?.nome && (
            <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
              {erros?.nome}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="venda-whats" className="text-sm font-medium text-foreground-800">
            WhatsApp
          </label>
          <input
            id="venda-whats"
            type="tel"
            value={telefone}
            onChange={(e) => {
              setTelefone(mascaraTelefone(e.target.value));
              setErros((er) => ({ ...er, whatsapp: undefined }));
            }}
            placeholder="(00) 00000-0000"
            inputMode="numeric"
            className={`mt-1.5 w-full rounded-2xl border bg-white px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 ${
              erros?.whatsapp ? 'border-danger-500' : 'border-secondary-300'
            }`}
          />
          {erros?.whatsapp && (
            <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
              {erros?.whatsapp}
            </p>
          )}
        </div>
      </div>

      {erro && (
        <div className="mt-4 rounded-2xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {erro}
        </div>
      )}

      <button
        type="button"
        onClick={gerarPix}
        disabled={gerando}
        className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {gerando && (
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
        )}
        {gerando ? 'Gerando…' : 'Gerar Pix'}
      </button>
    </div>
  );

  if (!t) return null;

  return (
    <ErrorBoundary
      mensagem="Não consegui abrir a venda agora."
      aoFechar={aoSair}
      prefixo="[NovaVenda]"
    >
      <Modal aberto titulo={tituloModal} aoFechar={cancelar}>
        {etapa === 'pronto' ? conteudoPronto : etapa === 'pix' ? conteudoPix : conteudoNova}
      </Modal>
    </ErrorBoundary>
  );
}